import { useEffect, useMemo, useState } from "react";

import {
  Add,
  ArrowBack,
  ArrowDownward,
  ArrowUpward,
  Delete,
  Edit,
  Search,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  deleteNotificationRule,
  listNotificationRule,
  getOrgConfigPublic,
  NotificationRuleModel,
  patchNotificationRule,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useLocation, useNavigate, useParams } from "react-router";

import NotificationRuleFormDialog, {
  type ReportTypeOption,
} from "./form-management/NotificationRuleFormDialog";
import {
  describeNotificationRule,
  groupNotificationRules,
  statusLabel,
  type NotificationRuleGroup,
} from "./notificationRules";

const AdvancedNotificationsPage = () => {
  const { orgId = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const settingsPath = location.pathname.replace(
    /\/settings\/advanced-notifications\/?$/,
    "/settings",
  );

  const [rules, setRules] = useState<NotificationRuleModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [reportTypes, setReportTypes] = useState<ReportTypeOption[]>([]);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [editingGroup, setEditingGroup] =
    useState<NotificationRuleGroup | null>(null);

  useEffect(() => {
    if (orgId) {
      fetchRules();
      fetchReportTypes();
    }
  }, [orgId]);

  const fetchRules = async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      const response = await listNotificationRule({ path: { org_id: orgId } });
      setRules(response.data || []);
    } catch (error) {
      console.error("Error fetching notification rules:", error);
      toast.error("Failed to load notification rules");
    } finally {
      setLoading(false);
    }
  };

  const fetchReportTypes = async () => {
    if (!orgId) return;
    try {
      const response = await getOrgConfigPublic({ path: { org_id: orgId } });
      const byClass = new Map<string, string>();
      for (const survey of response.data?.surveys ?? []) {
        if (survey.is_registry || !survey.survey_class) continue;
        if (!byClass.has(survey.survey_class)) {
          byClass.set(survey.survey_class, survey.name || survey.survey_class);
        }
      }
      setReportTypes([...byClass].map(([value, label]) => ({ value, label })));
    } catch (error) {
      console.error("Error fetching report types:", error);
    }
  };

  const reportTypeLabels = useMemo(
    () =>
      Object.fromEntries(reportTypes.map(({ value, label }) => [value, label])),
    [reportTypes],
  );

  const groups = useMemo(() => groupNotificationRules(rules), [rules]);

  const visibleGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return groups;
    return groups.filter((group) =>
      [
        group.name,
        ...group.emailAddresses,
        ...group.surveyClasses.map((c) => reportTypeLabels[c] ?? c),
        ...group.statuses.map(statusLabel),
        describeNotificationRule(group, reportTypeLabels),
      ]
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [groups, search, reportTypeLabels]);

  const handleAdd = () => {
    setEditingGroup(null);
    setFormOpen(true);
  };

  const handleEdit = (group: NotificationRuleGroup) => {
    setEditingGroup(group);
    setFormOpen(true);
  };

  const handleDelete = async (group: NotificationRuleGroup) => {
    if (!window.confirm(`Delete the notification rule "${group.name}"?`))
      return;
    try {
      for (const rule of group.rules) {
        await deleteNotificationRule({
          path: { org_id: orgId, rule_id: rule.id },
        });
      }
      toast.success("Rule deleted");
    } catch (error) {
      console.error("Error deleting notification rule:", error);
      toast.error("Failed to delete rule");
    } finally {
      // A group is several rows, so a mid-way failure leaves some of them behind.
      fetchRules();
    }
  };

  /** Every backing row of a group shares its priority, so the whole group moves as a unit. */
  const setGroupPriority = async (
    group: NotificationRuleGroup,
    priority: number,
  ) => {
    for (const rule of group.rules) {
      await patchNotificationRule({
        path: { org_id: orgId, rule_id: rule.id },
        body: { priority },
      });
    }
  };

  const nextPriority = Math.max(...groups.map((g) => g.priority), -1) + 1;

  const handleMovePriority = async (
    group: NotificationRuleGroup,
    direction: "up" | "down",
  ) => {
    const currentIndex = groups.findIndex((g) => g.key === group.key);
    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (currentIndex === -1 || newIndex < 0 || newIndex >= groups.length)
      return;

    const target = groups[newIndex];
    if (target.priority === group.priority) return;

    setReordering(true);
    try {
      // Park the target on an unused priority first. Swapping in two steps would briefly put both
      // groups on the same one, and the engine fires every rule at a priority — so the other
      // group's recipients would be emailed too, permanently if the second step then failed.
      await setGroupPriority(target, nextPriority);
      await setGroupPriority(group, target.priority);
      await setGroupPriority(target, group.priority);
    } catch (error) {
      console.error("Error reordering notification rule:", error);
      await Promise.allSettled([
        setGroupPriority(group, group.priority),
        setGroupPriority(target, target.priority),
      ]);
      toast.error(
        "Failed to reorder rule. The rule list has been refreshed — please check the priorities.",
      );
    } finally {
      setReordering(false);
      fetchRules();
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={() => navigate(settingsPath)} size="small">
              <ArrowBack />
            </IconButton>
            <Typography variant="h6">
              <b>Advanced Notifications</b>
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
            Add Rule
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Route status-change emails to specific recipients based on the report
          type, the action taken, and the submission&apos;s field values. Lower
          priority numbers win; default rules fire only when no conditional
          rules match.
        </Typography>

        <TextField
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search rules..."
          sx={{ mb: 2, maxWidth: 360 }}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={60}>Priority</TableCell>
                <TableCell>Rule</TableCell>
                <TableCell>Report Types</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Recipients</TableCell>
                <TableCell width={120} />
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleGroups.map((group) => {
                const index = groups.findIndex((g) => g.key === group.key);
                return (
                  <TableRow key={group.key}>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleMovePriority(group, "up")}
                          disabled={index === 0 || reordering}
                          aria-label={`Raise priority of ${group.name}`}
                        >
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                        <Typography variant="body2">
                          {group.priority}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleMovePriority(group, "down")}
                          disabled={index === groups.length - 1 || reordering}
                          aria-label={`Lower priority of ${group.name}`}
                        >
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        <b>{group.name}</b>
                      </Typography>
                      {group.isDefault && (
                        <Chip
                          label="Default"
                          size="small"
                          color="primary"
                          sx={{ my: 0.5 }}
                        />
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {describeNotificationRule(group, reportTypeLabels)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {group.surveyClasses.length === 0 ? (
                          <Chip label="All Reports" size="small" color="info" />
                        ) : (
                          group.surveyClasses.map((surveyClass) => (
                            <Chip
                              key={surveyClass}
                              label={
                                reportTypeLabels[surveyClass] ?? surveyClass
                              }
                              size="small"
                            />
                          ))
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {group.statuses.length === 0 ? (
                          <Chip label="Any Action" size="small" color="info" />
                        ) : (
                          group.statuses.map((status) => (
                            <Chip
                              key={status}
                              label={statusLabel(status)}
                              size="small"
                            />
                          ))
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {group.emailAddresses.map((email) => (
                          <Chip key={email} label={email} size="small" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(group)}
                        aria-label={`Edit ${group.name}`}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(group)}
                        color="error"
                        aria-label={`Delete ${group.name}`}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {visibleGroups.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {groups.length === 0
                        ? "No notification rules defined. Click “Add Rule” to create your first rule."
                        : "No rules match your search."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <NotificationRuleFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={fetchRules}
        orgId={orgId}
        reportTypes={reportTypes}
        defaultPriority={editingGroup ? editingGroup.priority : nextPriority}
        editingGroup={editingGroup}
      />
    </Box>
  );
};

export default AdvancedNotificationsPage;
